import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, EnvelopeSimple, Lock, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../utils/api';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // email, reset, done
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post('/auth/forgot-password', { email: email.trim() });
      if (data.reset_token) {
        setToken(data.reset_token);
        setStep('reset');
        toast.success('Reset token generated');
      } else {
        toast.success('If an account exists, a reset link has been sent');
        setStep('reset');
      }
    } catch {
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: newPassword });
      setStep('done');
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/login" className="flex items-center gap-2 text-[#9E9E9E] hover:text-white mb-6 transition-colors">
          <ArrowLeft size={16} /> Back to Login
        </Link>

        <div className="bg-[#161616] border border-[#262626] rounded-xl p-8">
          {step === 'email' && (
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#0096FF]/15 rounded-lg flex items-center justify-center">
                  <EnvelopeSimple size={20} weight="bold" className="text-[#0096FF]" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white" style={{fontFamily: 'Outfit'}}>Reset Password</h1>
                  <p className="text-[#9E9E9E] text-sm">Enter your email to get started</p>
                </div>
              </div>
              <form onSubmit={handleRequestReset} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Email Address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF]"
                    data-testid="reset-email-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-[#0096FF] text-white rounded-md font-bold hover:bg-[#0096FF]/80 transition-colors disabled:opacity-50"
                  data-testid="reset-request-btn"
                >
                  {loading ? 'Sending...' : 'Continue'}
                </button>
              </form>
            </>
          )}

          {step === 'reset' && (
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#FF9500]/15 rounded-lg flex items-center justify-center">
                  <Lock size={20} weight="bold" className="text-[#FF9500]" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white" style={{fontFamily: 'Outfit'}}>New Password</h1>
                  <p className="text-[#9E9E9E] text-sm">Enter your new password</p>
                </div>
              </div>
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Reset Token</label>
                  <input
                    type="text"
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    placeholder="Paste your reset token"
                    required
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#FF9500] font-mono"
                    data-testid="reset-token-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#F8F8F8] mb-2">New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    required
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#FF9500]"
                    data-testid="reset-new-password"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Confirm Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    required
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#FF9500]"
                    data-testid="reset-confirm-password"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-[#FF9500] text-black rounded-md font-bold hover:bg-[#FF9500]/80 transition-colors disabled:opacity-50"
                  data-testid="reset-submit-btn"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
            </>
          )}

          {step === 'done' && (
            <div className="text-center py-6">
              <CheckCircle size={48} weight="fill" className="text-[#34C759] mx-auto mb-4" />
              <h2 className="text-xl font-bold text-white mb-2" style={{fontFamily: 'Outfit'}}>Password Reset!</h2>
              <p className="text-[#9E9E9E] text-sm mb-6">You can now log in with your new password</p>
              <button
                onClick={() => navigate('/login')}
                className="w-full py-3 bg-[#0096FF] text-white rounded-md font-bold hover:bg-[#0096FF]/80 transition-colors"
                data-testid="go-to-login-btn"
              >
                Go to Login
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
